import { css } from '@emotion/react'
import styled from '@emotion/styled'
import { memo, useEffect, useRef } from 'react'
import { closeRoom, openRoomState } from '../../../../actions/room'
import { sendRoomMessage } from '../../../../actions/sendRoomMessage'
import { recoverFromKick } from '../../../../actions/user'
import { getRoomState, setRoomIDState } from '../../../../app-state/state.room'
import i18n from '../../../../utilities/i18n'
import Button from '../../../Buttons/Button'
import { useHealthAppStateSelector } from '../../../Hooks/useHealthAppState'
import { useRoomSelector } from '../../../Hooks/useRoomAppState'
import { useUserSelector } from '../../../Hooks/useUser'
import FlexGrid from '../../../common/FlexGrid'
import Chat from '../../../presentation/Chat/Rooms/Chat'
import Empty from '../../../presentation/Empty'
import { getThemeSpacing } from '../../../styles/appTheme'
import { palette } from '../../../styles/colors'
import { COMPONENT_HEIGHTS, PAPER_HEIGHTS } from '../../../theme'
import Compose from '../Compose'

const buttonOverrides = css({
    marginTop: getThemeSpacing(),
    color: palette.greenMFC,
    fontSize: '0.875rem',
    padding: `${getThemeSpacing(0.75)} ${getThemeSpacing(1)}`,
})

const rootStyles = css({
    display: 'flex',
    flexDirection: 'column',
    height: '100%',
})
const gridRootStyles = css({
    position: 'relative',
    overflow: 'hidden',
    height: '100%',
})
const chatGridStyles = css({
    position: 'relative',
    display: 'flex',
    flexDirection: 'column',
    flexGrow: 1,
    flexBasis: '100%',
    height: '100%',
    minHeight: 0,
    overflowX: 'hidden',
    overflowY: 'hidden',
    zIndex: PAPER_HEIGHTS.zero,
})
const chatPaneStyles = css({
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'flex-end',
})

const StyledEmptyMessage = styled('div')({
    display: 'flex',
    flexFlow: 'column nowrap',
    justifyContent: 'center',
    alignItems: 'center',
    textAlign: 'center',
    height: '100%',
    width: '100%',
    paddingTop: getThemeSpacing(4),
    paddingLeft: getThemeSpacing(2),
    paddingRight: getThemeSpacing(2),
    fontSize: 13,
    fontWeight: 500,
})

interface IRoom {
    id: number
    height: number
    onToggleWhiteboard(): void
}

const Room = memo((props: IRoom) => {
    const explicitClose = useRef(false)
    const chatContainerRef = useRef<HTMLDivElement>(null)
    const socketConnected = useHealthAppStateSelector('connected')

    const { height, id, onToggleWhiteboard } = props
    const isSubscribed = useRoomSelector(id, 'isSubscribed')
    const banned = useUserSelector(id, 'banned')
    const isKicked = useUserSelector(id, 'isKicked')

    const kickedOrBanned = useRef(isKicked || banned)
    const loaded = !!id && socketConnected
    const styleHeight = height - COMPONENT_HEIGHTS.compose
    const noAccessMessage = banned
        ? i18n.t('rooms.banned')
        : isKicked
          ? i18n.t('rooms.kicked')
          : ''

    useEffect(() => {
        if (!isSubscribed) {
            return
        }

        Object.values(getRoomState()).forEach(
            ({ isPinned, id: existingID }) => {
                if (!existingID || id === existingID || isPinned) {
                    return
                }

                closeRoom(existingID)
            },
        )
    }, [isSubscribed, id])

    useEffect(() => {
        return () => {
            recoverFromKick(id)
            explicitClose.current = false
            kickedOrBanned.current = false
        }
    }, [isSubscribed, id])

    useEffect(() => {
        kickedOrBanned.current = banned || isKicked
    }, [banned, isKicked])

    if (!id) {
        return null
    }

    return (
        <div css={rootStyles}>
            <FlexGrid
                overrides={gridRootStyles}
                direction="column"
                wrap="nowrap"
                key={id}
            >
                {(banned || isKicked) && <Empty before={noAccessMessage} />}
                {!isSubscribed && (
                    <StyledEmptyMessage>
                        You must join the room to access it from here.
                        <br />
                        <Button
                            size="small"
                            data-testid="Button__JoinRoom"
                            clean
                            border
                            css={buttonOverrides}
                            onClick={() => {
                                setRoomIDState(id)
                                openRoomState(id)
                            }}
                        >
                            Join Room
                        </Button>
                    </StyledEmptyMessage>
                )}
                {!!isSubscribed && !banned && !isKicked && (
                    <FlexGrid ref={chatContainerRef} overrides={chatGridStyles}>
                        <div css={chatPaneStyles}>
                            <Chat
                                id={id}
                                styleHeight={styleHeight}
                                onOpenWhiteboard={onToggleWhiteboard}
                                pm={false}
                            />
                            <Compose
                                id={id}
                                disabled={!loaded}
                                onSend={sendRoomMessage}
                                placeholder={i18n.t('chat.sendChatMessage')}
                                disableKeyboardOpenDetection
                                noFullWindowButtons
                                hideTipButton
                            />
                        </div>
                    </FlexGrid>
                )}
            </FlexGrid>
        </div>
    )
})

export default Room
